/**
 * User Profile Service
 *
 * Manages user profile data stored in Firestore.
 * Path: /users/{uid}/profile
 */

import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, Unsubscribe } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// =============================================================================
// Types
// =============================================================================

export interface UserProfile {
  /** User's full name (e.g., "Belle van Oostenbruggen") */
  fullName?: string;
  /** First name */
  firstName?: string;
  /** Last name */
  lastName?: string;
  /** How the user found us (optional, from signup) */
  howFoundUs?: string;
  /** Last updated timestamp */
  updatedAt?: Date;
}

// =============================================================================
// Service Functions
// =============================================================================

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
      return null;
    }

    const data = profileSnap.data();
    return {
      fullName: data.fullName,
      firstName: data.firstName,
      lastName: data.lastName,
      updatedAt: data.updatedAt?.toDate(),
    };
  } catch (error) {
    logger.error('[UserProfileService] Failed to get profile:', error);
    return null;
  }
}

/**
 * Update user profile in Firestore
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'updatedAt'>>
): Promise<void> {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');

    await setDoc(
      profileRef,
      {
        ...updates,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    logger.log('[UserProfileService] Profile updated for user:', userId);
  } catch (error) {
    logger.error('[UserProfileService] Failed to update profile:', error);
    throw error;
  }
}

/**
 * Watch user profile for real-time updates
 */
export function watchUserProfile(
  userId: string,
  callback: (profile: UserProfile | null) => void
): Unsubscribe {
  const profileRef = doc(db, 'users', userId, 'profile', 'data');

  return onSnapshot(
    profileRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      callback({
        fullName: data.fullName,
        firstName: data.firstName,
        lastName: data.lastName,
        updatedAt: data.updatedAt?.toDate(),
      });
    },
    (error) => {
      logger.error('[UserProfileService] Snapshot error:', error);
      callback(null);
    }
  );
}
